import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { DEVICE_SIZE, mq } from '../../../utils/mq';
import { useTranslation } from '../../../utils/translation';
import { Cta } from '../../cta';
import Con from '../../../icons/con';
import Pro from '../../../icons/pro';
import { MarkStars } from '../../mark-with-stars';
import PriceCard from './price-list';
import { RatingCursor } from '../../rating-cursor';
import DetailedSticker from '../../../icons/sticker/detailedSticker';

export type RatingCardProps = {
  SlateContent: React.FC<{ data: object }>;
  cell: any;
  eventCta?: (shopName?: string, price?: string) => void;
};

const RatingRow = styled.div`
  display: flex;
  flex-direction: column;
  margin: 15px 0 30px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin: 30px 0 0;
    `,
  )}
`;

const Wrapper = styled.div`
  border-radius: 10px;
  box-shadow: 0px 0px 30px 0px var(--color-box-shadow);
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin: 0 0 30px;
  padding: 15px 15px 0;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      gap: 45px;
      margin: 0 0 60px;
      padding: 45px 60px;
    `,
  )}
`;

const RatingHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
`;

const RatingHeaderMark = styled.div`
  align-items: baseline;
  display: flex;
  flex-direction: column;

  > * {
    align-items: start;
  }
`;

const RatingTitle = styled.div`
  font-family: var(--theme-typography-primary-font);
  font-size: 26px;
  font-weight: 700;
  line-height: 1.38;
  margin: 0 0 var(--spacer-s);
`;

const Title = styled.div`
  font-size: 16px;
  font-family: var(--theme-typography-primary-font);
  font-weight: 700;
  line-height: 1.38;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 22px;
    `,
  )}
`;

const LeftSide = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 15px;
`;

const RightSide = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 15px;
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  font-size: 16px;
  gap: 12px;
  list-style: none;

  svg {
    min-width: 22px;
  }
`;

const RatingProsAndCons = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacer-fluid);

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      flex-direction: row;
    `,
  )}
`;

const RatingMiddle = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacer-s);
`;

const ProItem = styled.li`
  align-items: start;
  display: flex;
  gap: var(--spacer-xs);
  line-height: 22px;
`;

const ConItem = styled.li`
  align-items: start;
  display: flex;
  gap: var(--spacer-xs);
  line-height: 22px;
`;

const RatingData = styled.div`
  display: flex;
  flex-flow: wrap;
  gap: var(--spacer-s) var(--spacer-s);
  justify-content: space-between;

  > * {
    width: 100%;

    ${mq.gte(
      DEVICE_SIZE.LARGE,
      css`
        width: 48%;
      `,
    )}
  }
`;

const ConclusionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: -var(--spacer-s);
`;

const Conclusion = styled.div`
  line-height: 22px;

  p:last-of-type {
    margin-bottom: 0;
  }
`;

const RatingMore = styled.div`
  align-items: center;
  display: flex;
  gap: var(--spacer-s);
`;

const AwardContainer = styled.div`
  ${mq.lte(DEVICE_SIZE.LARGE, css``)};
`;

const RatingHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacer-m);
  flex-grow: 1;
`;

const RatingHeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  margin-bottom: var(--spacer-xs);
  margin-right: var(--spacer-m);
  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      margin-right: var(--spacer-s);
    `,
  )};
`;

const RatingBottom = (props: React.HTMLAttributes<HTMLDivElement>) => <div {...props} />;

const RatingCard = ({ SlateContent, cell, eventCta }: RatingCardProps) => {
  const t = useTranslation();

  const {
    id,
    conclusionJson,
    detailedSticker,
    conclusion,
    scores,
    mainCtaInfos,
    pros,
    cons,
    rating,
    testLink,
    title,
    prices,
  } = cell.content.state.data;
  const contentConclusion = useMemo(() => {
    return conclusionJson ? <SlateContent data={conclusionJson} /> : <Conclusion>{conclusion}</Conclusion>;
  }, [conclusionJson, conclusion, SlateContent]);

  const ratingData = useMemo(
    () =>
      Object.keys(scores || []).map((key: string) => {
        return {
          label: key,
          value: scores[key],
        };
      }),
    [scores],
  );

  const newWindow = !mainCtaInfos.internalLink;
  const rel = newWindow ? ['nofollow'] : null;
  const serviceCtaProps = {
    href: mainCtaInfos?.link,
    openInNewWindow: newWindow,
    rel,
  };

  return (
    <RatingRow id={`conclusion-product-${id}`}>
      <Wrapper>
        <RatingHeader>
          <RatingHeaderLeft>
            <RatingTitle>{t('design.ratingBlock.conclusion')}</RatingTitle>
            <RatingHeaderMark>
              <Title>{t('design.ratingBlock.generalRating')}</Title>
              <MarkStars className="mod-dark" rating={rating} max={10} />
            </RatingHeaderMark>
          </RatingHeaderLeft>
          <RatingHeaderRight>
            {detailedSticker && (
              <AwardContainer>
                <DetailedSticker detailedSticker={detailedSticker} scale={0.2} />
              </AwardContainer>
            )}
          </RatingHeaderRight>
        </RatingHeader>
        {contentConclusion && (
          <ConclusionWrapper>
            {contentConclusion}
            {testLink?.href && (
              <RatingMore>
                <Cta className="mod-blue mod-empty" inline {...testLink}>
                  {t('design.product.readTest')}
                </Cta>
              </RatingMore>
            )}
          </ConclusionWrapper>
        )}
        <RatingProsAndCons>
          {pros?.length > 0 ? (
            <LeftSide>
              <Title>{t('design.ratingBlock.pros')}</Title>
              <List>
                {pros?.map((pro: string) => (
                  <ProItem key={pro}>
                    <Pro height={22} width={22} color="var(--theme-palette-green)" />
                    {pro}
                  </ProItem>
                ))}
              </List>
            </LeftSide>
          ) : null}
          {cons?.length > 0 ? (
            <RightSide>
              <Title>{t('design.ratingBlock.cons')}</Title>
              <List>
                {cons?.map((con: string) => (
                  <ConItem key={con}>
                    <Con height={22} width={22} color="var(--theme-palette-red)" />
                    {con}
                  </ConItem>
                ))}
              </List>
            </RightSide>
          ) : null}
        </RatingProsAndCons>
        {ratingData && ratingData.length > 0 ? (
          <RatingMiddle>
            <Title>{t('design.ratingBlock.subRating')}</Title>
            <RatingData>
              {ratingData.map((datum, index) => (
                <RatingCursor key={index} label={`${datum.label}`} value={datum.value} />
              ))}
            </RatingData>
          </RatingMiddle>
        ) : null}
        {mainCtaInfos.layout === 'online-service' ? (
          <Cta {...serviceCtaProps}>{t('design.serviceCardComparative.cta', { title })}</Cta>
        ) : null}
        {prices && prices.length > 0 && mainCtaInfos.layout !== 'online-service' ? (
          <RatingBottom>
            <Title>{t('design.ratingBlock.bestPrices')}</Title>
            <PriceCard prices={prices} isCompact eventCta={eventCta} itemId={id} />
          </RatingBottom>
        ) : null}
      </Wrapper>
    </RatingRow>
  );
};

export default RatingCard;
